import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';

const N8N_ACS_OID = process.env.REACT_APP_N8N_URL_ACS_OID;

// Clave del campo personalizado donde se guarda el OID de GenieACS (con el que se consulta el servicio)
const CLAVE_OID_GENIEACS = '1770612677';

type Host = { nombre?: string; ip?: string; mac?: string; tipo?: string; activo?: boolean };
type ServicioOid = { index: number; oid: string; perfil: string };
type Estado = 'loading' | 'ok' | 'error';

const getServiciosConOid = (clientData: any): ServicioOid[] => {
  const servicios = clientData?.servicios;
  if (!Array.isArray(servicios)) return [];
  return servicios
    .map((srv: any, index: number) => {
      try {
        const personalizados = typeof srv.personalizados === 'string' ? JSON.parse(srv.personalizados) : (srv.personalizados || {});
        return { index, oid: personalizados[CLAVE_OID_GENIEACS] || '', perfil: srv.perfil || `Servicio ${index + 1}` };
      } catch { return { index, oid: '', perfil: `Servicio ${index + 1}` }; }
    })
    .filter((s: ServicioOid) => s.oid);
};

/* ─────────────────────────────────────────────────────────────────────────────
   HISTORIAL LOCAL DE DISPOSITIVOS

   El router (TR-069) solo dice si un dispositivo está activo o no; NO da
   fechas. Para poder decir "desconectado hace 2 h" la app guarda, en este
   celular, cuándo vio cada dispositivo (por MAC) cada vez que se abre esta
   pantalla. Limitaciones honestas:
     - Solo registra cuando alguien abre la pantalla.
     - El historial vive en este dispositivo, no se comparte.
   ───────────────────────────────────────────────────────────────────────────── */
type RegistroDispositivo = {
  nombre?: string;
  primeraVez: number;            // primera vez que este celular lo vio
  ultimaVezConectado?: number;   // última vez que lo vio ACTIVO
  ultimaVezVisto: number;        // última vez que apareció (activo o no)
};
type Historial = Record<string, RegistroDispositivo>; // clave: MAC

const claveHistorial = (oid: string) => `sn_dispositivos_${oid}`;

const leerHistorial = (oid: string): Historial => {
  try {
    const raw = localStorage.getItem(claveHistorial(oid));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

/** Actualiza el historial con lo que acaba de responder el router y lo devuelve. */
const registrarEnHistorial = (oid: string, hosts: Host[], ahora: number): Historial => {
  const historial = leerHistorial(oid);
  for (const h of hosts) {
    const mac = (h.mac || '').toUpperCase();
    if (!mac) continue; // sin MAC no hay forma de reconocerlo la próxima vez
    const previo = historial[mac];
    const conectado = h.activo !== false;
    historial[mac] = {
      nombre: h.nombre || previo?.nombre,
      primeraVez: previo?.primeraVez ?? ahora,
      ultimaVezConectado: conectado ? ahora : previo?.ultimaVezConectado,
      ultimaVezVisto: ahora,
    };
  }
  try { localStorage.setItem(claveHistorial(oid), JSON.stringify(historial)); }
  catch { /* almacenamiento lleno o bloqueado: no es crítico */ }
  return historial;
};

/** "hace un momento", "hace 5 min", "hace 2 h", "hace 3 días" */
const hace = (desde: number, ahora: number): string => {
  const seg = Math.max(0, Math.round((ahora - desde) / 1000));
  if (seg < 60) return 'hace un momento';
  const min = Math.round(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
};

const DispositivosConectados: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Si un admin entra desde la ficha de un cliente (Clientes.tsx), usa los servicios de ESE
  // cliente (recibidos por navigate state); si no, usa la sesión propia del usuario logueado.
  const estadoNav = location.state as { servicios?: any[] } | undefined;
  const clientDataJSON = localStorage.getItem('clientData');
  const sesionPropia = clientDataJSON ? JSON.parse(clientDataJSON) : null;
  const clientData = estadoNav?.servicios ? { servicios: estadoNav.servicios } : sesionPropia;

  const serviciosOid = clientData ? getServiciosConOid(clientData) : [];
  const [servicioElegido, setServicioElegido] = useState<ServicioOid | null>(
    serviciosOid.length === 1 ? serviciosOid[0] : null
  );

  const [hosts, setHosts] = useState<Host[]>([]);
  const [historial, setHistorial] = useState<Historial>({});
  const [nuevos, setNuevos] = useState<Set<string>>(new Set()); // MACs nunca vistas antes de esta consulta
  const [consultadoEn, setConsultadoEn] = useState<number>(0); // momento de la última respuesta
  const [estado, setEstado] = useState<Estado>('loading');

  useEffect(() => {
    if (!servicioElegido || !N8N_ACS_OID) return;
    // Si el efecto se vuelve a ejecutar (StrictMode en desarrollo, o cambio de
    // servicio) la consulta anterior se descarta: así el historial se registra
    // UNA sola vez por consulta y "Nuevo" se calcula bien.
    let cancelada = false;
    setEstado('loading');
    axios.post(N8N_ACS_OID, { id_acs: servicioElegido.oid })
      .then(res => {
        if (cancelada) return;
        const lista: Host[] = res.data?.hosts || [];
        const ahora = Date.now();
        // "Nuevo" = tiene MAC y este equipo nunca la había registrado
        const previo = leerHistorial(servicioElegido.oid);
        const nuncaVistas = new Set(
          lista.map(h => (h.mac || '').toUpperCase()).filter(mac => mac && !previo[mac])
        );
        setHosts(lista);
        setNuevos(nuncaVistas);
        setHistorial(registrarEnHistorial(servicioElegido.oid, lista, ahora));
        setConsultadoEn(ahora);
        setEstado('ok');
      })
      .catch(() => { if (!cancelada) setEstado('error'); });
    return () => { cancelada = true; };
  }, [servicioElegido]);

  // Dos listas: lo que el router ve activo ahora, y lo que recuerda pero ya no está.
  const conectados = hosts.filter(h => h.activo !== false);
  const desconectados = hosts.filter(h => h.activo === false);

  const registroDe = (h: Host) => historial[(h.mac || '').toUpperCase()];

  const renderDispositivo = (h: Host, k: number, conectado: boolean) => {
    const reg = registroDe(h);
    // "Nuevo" = este celular nunca lo había visto antes de esta consulta
    const esNuevo = conectado && nuevos.has((h.mac || '').toUpperCase());
    // Para los desconectados: última vez que se le vio activo (si alguna vez se vio)
    const desconectadoDesde = !conectado && reg?.ultimaVezConectado
      ? hace(reg.ultimaVezConectado, consultadoEn)
      : null;

    return (
      <div
        key={k}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, background: 'var(--sn-superficie)',
          border: '1px solid var(--sn-borde)', borderRadius: 12, padding: '10px 12px',
          opacity: conectado ? 1 : 0.85,
        }}
      >
        <span style={{
          flexShrink: 0, width: 34, height: 34, borderRadius: '50%',
          background: conectado ? 'var(--sn-marca-suave)' : 'var(--sn-superficie-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
        }}>
          📱
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--sn-texto)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {h.nombre || reg?.nombre || 'Dispositivo desconocido'}
            </span>
            <span style={{ fontSize: 9, flexShrink: 0 }}>{conectado ? '🟢' : '⚪'}</span>
            {esNuevo && (
              <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, color: 'var(--sn-alerta-texto)', background: 'var(--sn-alerta-suave)', borderRadius: 999, padding: '1px 6px' }}>
                Nuevo
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
            {h.ip && <span>IP: {h.ip}</span>}
            {h.mac && <span>MAC: {h.mac}</span>}
            {h.tipo && <span>{h.tipo}</span>}
          </div>
          {!conectado && (
            <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginTop: 2 }}>
              {desconectadoDesde
                ? <>Desconectado {desconectadoDesde}</>
                : <>Desconectado · no se le vio conectado desde este equipo</>}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderListas = () => (
    <>
      <div style={{ fontSize: 12, color: 'var(--sn-texto-3)', fontWeight: 700, marginBottom: 10, display: 'flex', gap: 12, flexWrap: 'wrap' as const }}>
        <span>Conectados ahora: <span style={{ color: 'var(--sn-exito)' }}>{conectados.length}</span></span>
        <span>Desconectados recientemente: <span style={{ color: 'var(--sn-texto-2)' }}>{desconectados.length}</span></span>
      </div>

      {/* ── Conectados ahora ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sn-exito)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '6px 0 8px' }}>
        🟢 Conectados ahora
      </div>
      {conectados.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 14 }}>Ningún dispositivo conectado en este momento.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {conectados.map((h, k) => renderDispositivo(h, k, true))}
        </div>
      )}

      {/* ── Desconectados recientemente ── */}
      {desconectados.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '6px 0 8px' }}>
            ⚪ Desconectados recientemente
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {desconectados.map((h, k) => renderDispositivo(h, k, false))}
          </div>
          <p style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 10 }}>
            El router recuerda estos dispositivos unas horas después de desconectarse. Los tiempos se calculan desde las consultas hechas en este equipo.
          </p>
        </>
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />

      <div style={{ flex: 1, overflowY: 'auto' as const }}>
        <div style={{ background: 'var(--sn-superficie)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--sn-borde)', position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--sn-accion)' }}>
            <FaArrowLeft size={18} />
          </button>
          <div style={{ fontWeight: 700, fontSize: 15 }}>📱 Dispositivos Conectados</div>
        </div>

        <div style={{ padding: 16, maxWidth: 500, margin: '0 auto' }}>
          {serviciosOid.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 34 }}>⚠️</div>
              <p style={{ fontSize: 13, color: 'var(--sn-texto-3)', marginTop: 8 }}>No se encontró el router asociado a tu cuenta.</p>
            </div>
          ) : !servicioElegido ? (
            <>
              <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 14 }}>
                Tienes {serviciosOid.length} servicios. Elige a cuál quieres ver sus dispositivos conectados:
              </p>
              {serviciosOid.map((s) => (
                <button
                  key={s.index}
                  type="button"
                  onClick={() => setServicioElegido(s)}
                  style={{
                    width: '100%', textAlign: 'left', background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 12,
                    padding: '12px 14px', marginBottom: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <span style={{
                    flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: 'var(--sn-marca-suave)',
                    color: 'var(--sn-marca)', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {s.index + 1}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sn-texto)' }}>{s.perfil}</span>
                </button>
              ))}
            </>
          ) : (
            <>
              {serviciosOid.length > 1 && (
                <button
                  type="button"
                  onClick={() => setServicioElegido(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--sn-marca)', fontSize: 12, fontWeight: 600, padding: 0, marginBottom: 10, cursor: 'pointer' }}
                >
                  ← Elegir otro servicio
                </button>
              )}
              {serviciosOid.length > 1 && (
                <p style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginBottom: 10 }}>
                  Servicio: <strong>{servicioElegido.perfil}</strong>
                </p>
              )}

              {estado === 'loading' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sn-texto-3)', fontSize: 12, padding: '8px 0' }}>
                  <Spinner animation="border" size="sm" /> Consultando dispositivos…
                </div>
              ) : estado === 'error' ? (
                <p style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>No se pudieron consultar los dispositivos.</p>
              ) : hosts.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>No hay dispositivos conectados.</p>
              ) : (
                renderListas()
              )}
            </>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default DispositivosConectados;
